import { useState, useRef, useMemo } from "react";
import { interpolateViridis } from "d3-scale-chromatic";
import { HeaderWithModal, downloadFile, Modal } from "../utils.jsx";
import { useFetchProtModsDmo, useFetchModHeatmapDmo, useUnimod } from "../hooks.js";

// Distinct symbol + colour per modification type. Cycled by discovery order.
const PALETTE = ['#2563eb', '#dc2626', '#16a34a', '#9333ea', '#ea580c',
                 '#0891b2', '#ca8a04', '#db2777', '#4f46e5', '#65a30d'];
const SHAPES  = ['●', '■', '▲', '◆', '★', '✚', '▼', '⬟', '◐', '✦'];

// mod_category quality tiers -> colour used for the lollipop heads / ticks.
const CATEGORY = {
    0: {name: 'gold',   color: '#d4af37'},
    1: {name: 'silver', color: '#9ca3af'},
    2: {name: 'bronze', color: '#cd7f32'},
};
const catInfo = c => CATEGORY[c] || {name: 'n/a', color: '#94a3b8'};

// Unimod 'flag' values -> pill styles (see populate_unimod_db in
// digest_ms_results_v3.py).
const FLAG_INFO = {
    substitution: {
        name: 'substitution',
        pill: 'border-amber-300 bg-amber-50 text-amber-700',
    },
    residue_loss: {
        name: 'residue_loss',
        pill: 'border-violet-300 bg-violet-50 text-violet-700',
    },
};

// Single-letter code → full amino acid name, for the residue-type filter list.
const AMINO_ACID_NAMES = {
    A: 'Alanine', R: 'Arginine', N: 'Asparagine', D: 'Aspartic acid', C: 'Cysteine',
    E: 'Glutamic acid', Q: 'Glutamine', G: 'Glycine', H: 'Histidine', I: 'Isoleucine',
    L: 'Leucine', K: 'Lysine', M: 'Methionine', F: 'Phenylalanine', P: 'Proline',
    S: 'Serine', T: 'Threonine', W: 'Tryptophan', Y: 'Tyrosine', V: 'Valine',
};

const WINDOW = 40;   // residues shown in the magnified view
const CELL = 14;     // px per residue in the magnified view
const TRACK_W = 560; // px width of the full-length overview track

const modKey = m => {
    if (m.mass != null) return `M:${Number(m.mass).toFixed(4)}`;
    if (m.mod_mass_spec_id != null) return `ms:${m.mod_mass_spec_id}`;
    if (m.unimod_id != null) return `U:${m.unimod_id}`;
    return `mod:${m.mod_mass_spec_id}`;
};

// Max rows (studies/experiments) drawn in the web heatmap; mirrors the
// notebook's y_web_display. Denser matrices are truncated to the top groups.
const HEATMAP_MAX_ROWS = 50;
const HM_CELL = 20;       // px per matrix cell
const HM_ROW_LABEL_W = 150;
const HM_COL_LABEL_H = 96;

// Column label: "specificity [mass]" like the notebook's spec_mass, falling
// back to the modification full name / Unimod accession / id.
const modColumnLabel = c => {
    if (c.specificity && c.mass != null) return `${c.specificity} [${Number(c.mass).toFixed(2)}]`;
    if (c.full_name) return c.full_name;
    if (c.unimod_id != null) return `U:${c.unimod_id}`;
    return `mod ${c.modification_id}`;
};

// Study/experiment x modification detection heatmap, rendered under the PTM
// lollipop figure. Each row is normalized by its own total (relative abundance
// per group), matching norm_by_y() in mod_heatmaps.ipynb.
//
// `selectedUnimodIds` is the set of Unimod accessions currently selected in the
// legend above; the heatmap is built from only those modifications (columns are
// filtered and every row total is recomputed over the remaining columns).
const ModHeatmap = ({species_sn, protein_seq_id, selectedUnimodIds}) => {
    const {isReady, group_column, rows, columns, matrix} = useFetchModHeatmapDmo({species_sn, protein_seq_id});

    // Restrict to the selected modifications, drop now-empty groups, and
    // re-order rows & columns by their (recomputed) totals so the densest
    // signal stays top-left.
    const view = useMemo(() => {
        const keep = columns
            .map((c, j) => [c, j])
            .filter(([c]) => selectedUnimodIds == null || selectedUnimodIds.has(c.unimod_id));

        const keptColumns = keep.map(([c]) => c);
        const keptJ = keep.map(([, j]) => j);

        // Per-group cells + recomputed total over the kept columns.
        const groupRows = rows
            .map((r, i) => {
                const cells = keptJ.map(j => matrix[i][j]);
                return {id: r.id, label: r.label, cells, total: cells.reduce((a, b) => a + b, 0)};
            })
            .filter(gr => gr.total > 0)
            .sort((a, b) => b.total - a.total);

        // Column ordering by total across the surviving rows.
        const colTotals = keptColumns.map((_, k) => groupRows.reduce((s, gr) => s + gr.cells[k], 0));
        const order = keptColumns.map((_, k) => k).sort((a, b) => colTotals[b] - colTotals[a]);

        return {
            columns: order.map(k => keptColumns[k]),
            rows: groupRows.map(gr => ({
                id: gr.id, label: gr.label, total: gr.total,
                cells: order.map(k => gr.cells[k]),
            })),
        };
    }, [rows, columns, matrix, selectedUnimodIds]);

    const shownRows = view.rows.slice(0, HEATMAP_MAX_ROWS);

    const svgW = HM_ROW_LABEL_W + view.columns.length * HM_CELL + 8;
    const svgH = HM_COL_LABEL_H + shownRows.length * HM_CELL + 8;

    const cellColor = (count, rowTotal) => {
        if (!count || !rowTotal) return "#ffffff";           // 0 -> white (as in the notebook cmap)
        return interpolateViridis(Math.min(1, count / rowTotal));
    };

    // Export the full raw count matrix for the selected modifications (all
    // groups, not just the displayed rows) as a TSV: one row per
    // study/experiment, one column per modification.
    const downloadTsv = e => {
        e.preventDefault();
        const header = [group_column, "total_psms", ...view.columns.map(modColumnLabel)];
        const body = view.rows.map(r =>
            [r.label, r.total, ...r.cells].join("\t")
        );
        downloadFile({
            data: [header.join("\t"), ...body].join("\n"),
            fileName: `ptm_heatmap_${protein_seq_id}.tsv`,
            fileType: "text/tab-separated-values",
        });
    };

    return (
        <div className="mt-6 border-t border-slate-200 pt-4">
            <HeaderWithModal title="Modification detection across studies">
                <p>Relative abundance of each detected modification across the studies (or experiments, when this protein was reported by a single study) in which this protein was identified.</p>
                <p>Only the modifications currently selected in the legend above are included. Each row is normalized by its own total number of supporting PSMs, so colours show the within-study distribution of modification types rather than raw counts. Ported from the mod_heatmaps analysis.</p>
            </HeaderWithModal>

            {!isReady ? (
                <div className="mt-4 h-40 animate-pulse rounded bg-slate-100" />
            ) : view.rows.length === 0 || view.columns.length === 0 ? (
                <div className="mt-4 rounded border border-dashed border-slate-300 bg-slate-50 p-6 text-slate-500">
                    No modification detections available to build a heatmap for the selected PTMs.
                </div>
            ) : (
                <div className="mt-3">
                    <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
                        <span>
                            Rows: {group_column === "study_id" ? "study" : "experiment"} (total selected PSMs) &nbsp;•&nbsp;
                            showing {shownRows.length}/{view.rows.length}
                        </span>
                        <button
                            type="button"
                            onClick={downloadTsv}
                            className="font-medium text-blue-600 hover:underline dark:text-blue-500"
                        >
                            download raw data (tsv)
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <svg width={svgW} height={svgH} className="select-none">
                            {/* column headers (modification labels), rotated */}
                            {view.columns.map((c, j) => {
                                const x = HM_ROW_LABEL_W + j * HM_CELL + HM_CELL / 2;
                                return (
                                    <text key={`c${j}`} x={x} y={HM_COL_LABEL_H - 4}
                                          fontSize="9" fill="#475569"
                                          transform={`rotate(-45 ${x} ${HM_COL_LABEL_H - 4})`}>
                                        {modColumnLabel(c)}
                                    </text>
                                );
                            })}
                            {/* rows */}
                            {shownRows.map((r, i) => {
                                const y = HM_COL_LABEL_H + i * HM_CELL;
                                return (
                                    <g key={`r${i}`}>
                                        <text x={HM_ROW_LABEL_W - 6} y={y + HM_CELL / 2}
                                              textAnchor="end" dominantBaseline="central"
                                              fontSize="9" fill="#475569">
                                            {r.label} ({r.total})
                                        </text>
                                        {view.columns.map((c, j) => {
                                            const count = r.cells[j];
                                            const x = HM_ROW_LABEL_W + j * HM_CELL;
                                            const frac = r.total ? count / r.total : 0;
                                            return (
                                                <rect key={`cell${i}-${j}`}
                                                      x={x} y={y} width={HM_CELL} height={HM_CELL}
                                                      fill={cellColor(count, r.total)}
                                                      stroke="#e2e8f0" strokeWidth={0.5}>
                                                    <title>{`${r.label} • ${modColumnLabel(c)}\n${count} PSMs (${(frac * 100).toFixed(1)}% of row)`}</title>
                                                </rect>
                                            );
                                        })}
                                    </g>
                                );
                            })}
                        </svg>
                    </div>
                    {/* colour legend: relative abundance 0..1 (viridis) */}
                    <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                        <span>Relative abundance (per row)</span>
                        <span>0</span>
                        <span className="inline-block h-3 w-40 rounded"
                              style={{background: `linear-gradient(to right, ${
                                  [0, 0.25, 0.5, 0.75, 1].map(t => interpolateViridis(t)).join(", ")
                              })`}} />
                        <span>1</span>
                    </div>
                </div>
            )}
        </div>
    );
};

const PtmViewer = ({species_sn, protein_seq_id, protein_sequence}) => {
    const {isReady, prot_mods, detected_peptides, modified_peptides} = useFetchProtModsDmo({species_sn, protein_seq_id});

    const seq = protein_sequence || "";
    const L = seq.length;
    const maxStart = Math.max(0, L - WINDOW);

    const [windowStart, setWindowStart] = useState(0);
    const [selectedCategories, setSelectedCategories] = useState(() => new Set([0, 1, 2]));
    const [deselectedTypes, setDeselectedTypes] = useState(() => new Set());
    const [showUnimodModal, setShowUnimodModal] = useState(false);
    const [selectedPos, setSelectedPos] = useState(null);   // 1-based protein position shown in the residue details modal
    const [selectedAa, setSelectedAa] = useState(null);     // single-letter residue-type filter; null = show all
    const trackRef = useRef(null);

    const toggleCategory = cat => {
        setSelectedCategories(prev => {
            const next = new Set(prev);
            if (next.has(cat)) next.delete(cat); else next.add(cat);
            return next;
        });
    };

    const toggleType = key => {
        setDeselectedTypes(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    // Only PTMs in the selected categories.
    const categoryMods = useMemo(
        () => prot_mods.filter(m => selectedCategories.has(m.mod_category)),
        [prot_mods, selectedCategories]
    );

    // Residue letter a modification sits on (0-based indexing, matching how the
    // lollipop figure marks seq[mod_prot_pos] as modified).
    const aaAtPos = p => (p != null && p >= 0 && p < L) ? seq[p] : null;

    // categoryMods narrowed to the selected residue type. Keeps every
    // modification type so types deselected in the legend still appear dimmed;
    // the figure-level filter (aaFilteredMods) additionally drops deselected
    // types below.
    const aaMods = useMemo(
        () => selectedAa ? categoryMods.filter(m => aaAtPos(m.mod_prot_pos) === selectedAa) : categoryMods,
        [categoryMods, selectedAa, seq, L]
    );

    // Canonical Unimod metadata + mass classes. All entries sharing a
    // mod_mass_spec_id are indistinguishable by (specificity, mass) in DMO
    // searches, so each is a valid candidate for a detected modification.
    const {rows: unimodRows} = useUnimod({species_sn});

    // Unimod 'flag' strings (may be empty or "|"-separated), keyed by the
    // Unimod accession so each legend row can render its flag pills. The API
    // exposes the column as `flag` (falling back to `flags` just in case).
    const unimodIdToFlags = useMemo(() => {
        const m = new Map();
        for (const u of unimodRows || []) {
            if (u.unimod_id == null) continue;
            const flagStr = u.flag ?? u.flags ?? "";
            if (!flagStr) continue;
            for (const f of String(flagStr).split("|")) {
                const flag = f.trim();
                if (!flag) continue;
                if (!m.has(u.unimod_id)) m.set(u.unimod_id, new Set());
                m.get(u.unimod_id).add(flag);
            }
        }
        return m;
    }, [unimodRows]);

    // Unimod accession → canonical description, used for hover tooltips on
    // accession links (each ID in a mass class may have a different name).
    const unimodIdToName = useMemo(() => {
        const m = new Map();
        for (const u of unimodRows || []) {
            if (u.unimod_id == null) continue;
            m.set(Number(u.unimod_id), u.full_name || null);
        }
        return m;
    }, [unimodRows]);

    // Assign a symbol/colour to each distinct displayed mass (stable order).
    // prot_mods_dmo left-joins the unimod table on mod_mass_spec_id, so one
    // physical detection can arrive as several candidate rows (one per Unimod
    // accession of the same specificity+mass), and distinct specificity+mass
    // classes can share the same nominal mass. Grouping by the displayed mass
    // (4 decimals) merges all of them; count is the distinct protmod_id (true
    // detections).
    const {types, typeByKey, typesByKey} = useMemo(() => {
        const byKey = new Map();
        for (const m of aaMods) {
            const k = modKey(m);
            if (!byKey.has(k)) {
                const i = byKey.size;
                byKey.set(k, {
                    key: k,
                    mod_mass_spec_id: m.mod_mass_spec_id,
                    unimod_id: m.unimod_id,
                    full_name: m.full_name,
                    unimod_link: m.unimod_link,
                    mass: m.mass,
                    color: PALETTE[i % PALETTE.length],
                    shape: SHAPES[i % SHAPES.length],
                    protmodIds: new Set(),
                    unimodIds: new Set(),
                    count: 0,
                    sites: new Set(),
                    flags: new Set(),
                });
            }
            const entry = byKey.get(k);
            // Residue this modification is observed on (0-based indexing, the
            // same convention the figure and the amino-acid filter use).
            const aa = aaAtPos(m.mod_prot_pos);
            if (aa) entry.sites.add(aa);
            entry.protmodIds.add(m.protmod_id);
            if (m.unimod_id != null) entry.unimodIds.add(m.unimod_id);
            for (const f of (unimodIdToFlags.get(m.unimod_id) || [])) entry.flags.add(f);
        }
        const types = Array.from(byKey.values()).map(e => {
            const {protmodIds, unimodIds, ...rest} = e;
            return {
                ...rest,
                count: protmodIds.size,
                unimod_ids: [...unimodIds].sort((a, b) => a - b),
                flags: [...e.flags].sort(),
            };
        });
        // Key → enriched type entry (same objects as `types`, which carry the
        // resolved `unimod_ids` / `flags` arrays), for per-modification lookups.
        const typesByKey = new Map(types.map(t => [t.key, t]));
        return {types, typeByKey: byKey, typesByKey};
    }, [aaMods, seq, unimodIdToFlags]);

    // Further filter by selected types.
    const drawnMods = useMemo(
        () => categoryMods.filter(m => !deselectedTypes.has(modKey(m))),
        [categoryMods, deselectedTypes]
    );

    // Residue types carrying PTMs among the currently drawn mods, for the
    // "Amino acid" filter dropdown (sorted by single-letter code).
    const aaOptions = useMemo(() => {
        const s = new Set();
        for (const m of drawnMods) {
            const aa = aaAtPos(m.mod_prot_pos);
            if (aa) s.add(aa);
        }
        return Array.from(s).sort();
    }, [drawnMods, seq, L]);

    // drawnMods narrowed to the selected residue type (no-op for "show all").
    const aaFilteredMods = useMemo(
        () => selectedAa ? drawnMods.filter(m => aaAtPos(m.mod_prot_pos) === selectedAa) : drawnMods,
        [drawnMods, selectedAa, seq, L]
    );

    // Unimod accessions of the currently selected (visible) modifications;
    // drives which columns the detection heatmap below is built from.
    const selectedUnimodIds = useMemo(
        () => new Set(aaFilteredMods.map(m => m.unimod_id).filter(v => v != null)),
        [aaFilteredMods]
    );

    // Index PTMs by protein position (0-based, as used by the figure).
    const ptmsByPos = useMemo(() => {
        const map = new Map();
        for (const m of aaFilteredMods) {
            const pos = m.mod_prot_pos;
            if (!map.has(pos)) map.set(pos, []);
            map.get(pos).push(m);
        }
        return map;
    }, [aaFilteredMods]);

    // Details for the residue selected by clicking on a modified residue letter
    // (all PTMs at that position + a short sequence context window).
    const residueDetail = useMemo(() => {
        if (selectedPos == null) return null;
        const pos0 = selectedPos - 1;                     // 0-based
        const mods = ptmsByPos.get(pos0) || [];
        const aa = seq[pos0] ?? "?";
        const W = 7;                                      // residues shown on each side
        const lo = Math.max(0, pos0 - W);
        const hi = Math.min(L, pos0 + W + 1);
        return {
            pos: selectedPos,
            aa,
            mods,
            before: seq.slice(lo, pos0),
            after: seq.slice(pos0 + 1, hi),
            hasBefore: lo > 0,
            hasAfter: hi < L,
        };
    }, [selectedPos, ptmsByPos, seq, L]);

    const clampStart = s => Math.max(0, Math.min(maxStart, s));

    // Move the window so it is centred on the residue under clientX on the track.
    const moveToClientX = clientX => {
        const el = trackRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const frac = (clientX - rect.left) / rect.width;
        const centerResidue = Math.round(frac * L);
        setWindowStart(clampStart(centerResidue - Math.floor(WINDOW / 2)));
    };

    const onTrackPointerDown = e => {
        e.preventDefault();
        moveToClientX(e.clientX);
        const onMove = ev => moveToClientX(ev.clientX);
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    const posToTrackX = pos => (pos / Math.max(1, L)) * TRACK_W;

    const windowEnd = Math.min(L, windowStart + WINDOW); // exclusive residue index
    const visible = seq.slice(windowStart, windowEnd);

    // Global max PSM count so lollipop heights stay comparable while sliding.
    const maxPsm = useMemo(
        () => drawnMods.reduce((mx, m) => Math.max(mx, m.psm_count || 0), 0),
        [drawnMods]
    );

    const rulerY = 12;
    const letterY = 30;
    const plotTop = 40;      // top of the lollipop plot area
    const plotH = 72;        // vertical space for stems
    const plotBottom = plotTop + plotH;
    const PAD_L = 30;        // left margin for the PSM y-axis
    const magW = PAD_L + WINDOW * CELL;
    const magH = plotBottom + 10;

    // x of residue i (0-based within the window), accounting for the axis margin.
    const cellX = i => PAD_L + i * CELL + CELL / 2;

    // Scale a PSM count to a stem height (min 6px so single-PSM sites show).
    const psmToHeight = n => (maxPsm > 0 ? Math.max(6, (n / maxPsm) * plotH) : 6);

    // "Nice" integer y-axis ticks from 0..maxPsm.
    const yTicks = useMemo(() => {
        if (maxPsm <= 0) return [0];
        const target = 4;
        const raw = maxPsm / target;
        const mag = Math.pow(10, Math.floor(Math.log10(raw)));
        const step = Math.max(1, [1, 2, 5, 10].map(s => s * mag).find(s => s >= raw) || mag * 10);
        const ticks = [];
        for (let v = 0; v <= maxPsm + 1e-9; v += step) ticks.push(Math.round(v));
        if (ticks[ticks.length - 1] !== maxPsm) ticks.push(maxPsm);
        return Array.from(new Set(ticks));
    }, [maxPsm]);

    const modifiedResidues = ptmsByPos.size; // distinct modified protein positions

    return (
        <div className="shadow-[0px_0px_6px_0px_rgba(0,0,0,0.2)] rounded p-3 mt-3 mr-3">
            <div className="border-b width-full border-slate-200">
                <HeaderWithModal title="Post-Translational Modifications">
                    <p>Visualization of post-translational modifications (PTMs) detected on this protein.</p>
                    <p>Slide the cursor along the protein to magnify a region. Under the sequence, a lollipop marks each PTM site: its height is the number of supporting PSMs and its colour is the confidence category (gold/silver/bronze). The symbol shape identifies the modification type — see the legend for its Unimod accession. Click a dark modified residue to see every modification reported at that position. Use the <b>Amino acid</b> dropdown to narrow the whole figure (and the detection heatmap below) to the modifications found on a single residue type.</p>
                </HeaderWithModal>
            </div>

            {isReady && (
                <div className="mt-3 flex gap-8">
                    <div>
                        <div className="text-2xl font-semibold text-slate-900">{modifiedResidues}<span className="text-slate-400">/{L}</span></div>
                        <div className="text-xs text-slate-500">modified residues</div>
                    </div>
                    <div>
                        <div className="text-2xl font-semibold text-slate-900">{modified_peptides}<span className="text-slate-400">/{detected_peptides}</span></div>
                        <div className="text-xs text-slate-500">modified peptides</div>
                    </div>
                </div>
            )}

            {!isReady ? (
                <div className="mt-4 h-40 animate-pulse rounded bg-slate-100" />
            ) : L === 0 ? (
                <div className="mt-4 text-slate-500 text-sm">No protein sequence available.</div>
            ) : prot_mods.length === 0 ? (
                <div className="mt-4 rounded border border-dashed border-slate-300 bg-slate-50 p-6 text-slate-500">
                    No post-translational modifications reported for this protein.
                </div>
            ) : (
                <div className="mt-4 flex flex-row items-start gap-2">
                    {/* left: overview track + magnified region */}
                    <div className="min-w-0 flex-1 overflow-x-auto">
                        {/* full-length overview with draggable cursor */}
                        <svg
                            ref={trackRef}
                            width={TRACK_W}
                            height={34}
                            className="cursor-pointer select-none"
                            onPointerDown={onTrackPointerDown}
                        >
                            {/* protein backbone */}
                            <line x1={0} y1={17} x2={TRACK_W} y2={17} stroke="#cbd5e1" strokeWidth={4} strokeLinecap="round" />
                            {/* PTM ticks along the whole length (coloured by category) */}
                            {aaFilteredMods.map((m, i) => (
                                <line key={i}
                                    x1={posToTrackX(m.mod_prot_pos)} y1={9}
                                    x2={posToTrackX(m.mod_prot_pos)} y2={25}
                                    stroke={catInfo(m.mod_category).color} strokeWidth={1.5} />
                            ))}
                            {/* window cursor */}
                            <rect
                                x={(windowStart / Math.max(1, L)) * TRACK_W}
                                y={4}
                                width={(WINDOW / Math.max(1, L)) * TRACK_W}
                                height={26}
                                fill="#3b82f6" fillOpacity={0.18}
                                stroke="#3b82f6" strokeWidth={1.5} rx={2}
                            />
                        </svg>
                        <div className="text-xs text-slate-400">
                            residues {windowStart + 1}–{windowEnd} of {L}
                        </div>

                        {/* magnified sequence + PTM lollipop plot */}
                        <svg width={magW} height={magH} className="mt-2 select-none">
                            {/* position ruler: label every 10 residues */}
                            {visible.split('').map((_, i) => {
                                const pos = windowStart + i + 1;
                                if (pos % 10 !== 0) return null;
                                const x = cellX(i);
                                return (
                                    <g key={`r${i}`}>
                                        <text x={x} y={rulerY} textAnchor="middle" fontSize="8" fill="#94a3b8">{pos}</text>
                                        <line x1={x} y1={rulerY + 2} x2={x} y2={rulerY + 6} stroke="#cbd5e1" />
                                    </g>
                                );
                            })}
                            {/* residues: modified ones are clickable to inspect their PTMs */}
                            {visible.split('').map((aa, i) => {
                                const absPos = windowStart + i;
                                const displayPos = absPos + 1;
                                const modified = ptmsByPos.has(absPos);
                                const x = cellX(i);
                                if (!modified) {
                                    return (
                                        <text key={`a${i}`} x={x} y={letterY}
                                            textAnchor="middle" fontFamily="monospace" fontSize="14" fill="#64748b"
                                        >{aa}</text>
                                    );
                                }
                                return (
                                    <g key={`a${i}`} className="ptm-residue-group" role="button"
                                       aria-label={`Show modifications at residue ${aa} ${displayPos}`}
                                       title={`Click to show all modifications at position ${displayPos}`}
                                       onClick={() => setSelectedPos(displayPos)}>
                                        <circle className="ptm-residue-hit" cx={x} cy={letterY} r={8.5} />
                                        <text className="ptm-residue-text" x={x} y={letterY}
                                            textAnchor="middle" fontFamily="monospace" fontSize="14" fill="#0f172a" fontWeight={700}
                                        >{aa}</text>
                                    </g>
                                );
                            })}
                            {/* PSM y-axis */}
                            <line x1={PAD_L} y1={plotTop} x2={PAD_L} y2={plotBottom} stroke="#cbd5e1" />
                            {yTicks.map((v, k) => {
                                const y = plotBottom - (maxPsm > 0 ? (v / maxPsm) * plotH : 0);
                                return (
                                    <g key={`yt${k}`}>
                                        <line x1={PAD_L - 3} y1={y} x2={PAD_L} y2={y} stroke="#cbd5e1" />
                                        <text x={PAD_L - 5} y={y} textAnchor="end" dominantBaseline="central" fontSize="8" fill="#94a3b8">{v}</text>
                                    </g>
                                );
                            })}
                            <text x={8} y={plotTop - 4} fontSize="8" fill="#94a3b8">PSMs</text>
                            {/* plot baseline */}
                            <line x1={PAD_L} y1={plotBottom} x2={magW} y2={plotBottom} stroke="#e2e8f0" />
                            {/* lollipops: height = psm_count, head colour = mod_category */}
                            {visible.split('').flatMap((_, i) => {
                                const absPos = windowStart + i;          // 0-based protein position
                                const displayPos = absPos + 1;           // 1-based for display
                                const mods = ptmsByPos.get(absPos) || [];
                                const n = mods.length;
                                const xBase = cellX(i);
                                return mods.map((m, j) => {
                                    const t = typeByKey.get(modKey(m));
                                    const cat = catInfo(m.mod_category);
                                    const x = xBase + (j - (n - 1) / 2) * 8;
                                    const headY = plotBottom - psmToHeight(m.psm_count);
                                    return (
                                        <g key={`m${i}-${j}`}>
                                            <line x1={x} y1={plotBottom} x2={x} y2={headY} stroke={cat.color} strokeWidth={1.5} />
                                            <text x={x} y={headY} textAnchor="middle" dominantBaseline="central"
                                                  fontSize="12" fill={cat.color}>{t.shape}</text>
                                            <title>{`${t.key}${t.full_name ? ' – ' + t.full_name : ''} @ ${displayPos} • ${m.psm_count} PSMs • ${cat.name}`}</title>
                                        </g>
                                    );
                                });
                            })}
                        </svg>
                    </div>

                    {/* right: legend table */}
                    <div className="min-w-0 w-[34rem] shrink-0">
                        {/* amino-acid type filter (dropdown) */}
                        <div className="mb-2 flex items-center gap-2 text-xs text-slate-500">
                            <span className="font-medium">Amino acid:</span>
                            <select
                                value={selectedAa ?? ""}
                                onChange={e => setSelectedAa(e.target.value || null)}
                                title="Show only the modifications reported on a given residue type"
                                className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-700"
                            >
                                <option value="">show all</option>
                                {aaOptions.map(aa => (
                                    <option key={aa} value={aa}>
                                        {aa} — {AMINO_ACID_NAMES[aa] || aa}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {/* category colour key (click to filter) */}
                        <div className="mb-2 flex items-center gap-2 text-xs text-slate-500">
                            <span className="font-medium">Category:</span>
                            {[0, 1, 2].map(c => {
                                const active = selectedCategories.has(c);
                                return (
                                    <button
                                        key={c}
                                        type="button"
                                        onClick={() => toggleCategory(c)}
                                        title={`${active ? 'Hide' : 'Show'} ${catInfo(c).name} PTM sites`}
                                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 transition
                                            ${active
                                                ? 'border-slate-300 bg-white text-slate-800'
                                                : 'border-slate-200 bg-slate-50 text-slate-400 opacity-60 line-through'}`}
                                    >
                                        <span className="inline-block h-3 w-3 rounded-full" style={{background: catInfo(c).color, opacity: active ? 1 : 0.4}} />
                                        {catInfo(c).name}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="max-h-80 overflow-auto rounded border border-slate-100">
                            <table className="w-full text-sm">
                                <thead className="sticky top-0 bg-white">
                                    <tr className="border-b border-slate-200 text-left text-slate-500">
                                        <th className="py-1 pl-2 pr-2 font-medium w-8">
                                            {types.length > 1 && (
                                                <span className="flex items-center gap-1 font-normal normal-case text-blue-600">
                                                    <button type="button"
                                                        onClick={() => setDeselectedTypes(new Set())}
                                                        className="hover:underline">all</button>
                                                    <span className="text-slate-300">/</span>
                                                    <button type="button"
                                                        onClick={() => setDeselectedTypes(new Set(types.map(t => t.key)))}
                                                        className="hover:underline">none</button>
                                                </span>
                                            )}
                                        </th>
                                        <th className="py-1 pr-2 font-medium">Symbol</th>
                                        <th
                                            className="py-1 pr-2 font-medium cursor-pointer hover:underline"
                                            onClick={() => setShowUnimodModal(true)}
                                            title="Why may more than one UniMod ID be listed?"
                                        >
                                            Modification
                                            <svg aria-hidden="true" className="inline h-3 w-3 ml-0.5 text-slate-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
                                            </svg>
                                        </th>
                                        <th className="py-1 pr-2 font-medium text-right">Mass</th>
                                        <th className="py-1 pr-2 font-medium text-right">Sites</th>
                                        <th className="py-1 pr-2 font-medium">Flags</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {types.map(t => {
                                        const active = !deselectedTypes.has(t.key);
                                        return (
                                            <tr key={t.key}
                                                className={`border-b border-slate-100 last:border-0 cursor-pointer select-none transition
                                                    ${active ? 'hover:bg-slate-50' : 'bg-slate-50 opacity-50'}`}
                                                onClick={() => toggleType(t.key)}
                                            >
                                                <td className="py-1 pl-2 pr-2">
                                                    <input type="checkbox" checked={active} readOnly
                                                           className="pointer-events-none h-3.5 w-3.5 rounded border-slate-300 accent-blue-600" />
                                                </td>
                                                <td className="py-1 pr-2 text-lg text-slate-600">{t.shape}</td>
                                                <td className="py-1 pr-2" title={t.full_name || undefined}>
                                                    {t.unimod_ids.length > 0 ? (
                                                        <span className="flex flex-wrap gap-x-1">
                                                            {t.unimod_ids.map(uid => (
                                                                <a key={uid}
                                                                   href={t.unimod_id === uid && t.unimod_link ? t.unimod_link : `https://www.unimod.org/modifications_view.php?editid1=${uid}`}
                                                                   target="_blank" rel="noopener noreferrer"
                                                                   onClick={e => e.stopPropagation()}
                                                                   className="text-blue-600 hover:underline">
                                                                    U:{uid}
                                                                </a>
                                                            ))}
                                                        </span>
                                                    ) : <span className="text-slate-400">{t.full_name || '—'}</span>}
                                                </td>
                                                <td className="py-1 pr-2 text-right font-mono">{typeof t.mass === 'number' ? t.mass.toFixed(4) : <span className="text-slate-400">—</span>}</td>
                                                <td className="py-1 pr-2 text-right font-mono">{t.count}
                                                    {t.sites.size > 0 && (
                                                        <span className="ml-1 font-sans text-slate-400" title="Modified residue(s)">
                                                            ({[...t.sites].sort().join("")})
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="py-1 pr-2">
                                                    {t.flags.length > 0 ? (
                                                        <div className="flex flex-wrap gap-1">
                                                            {t.flags.map(f => {
                                                                const info = FLAG_INFO[f] || {name: f, pill: 'border-slate-300 bg-slate-50 text-slate-600'};
                                                                return (
                                                                    <span key={f} className={`rounded-full border px-1.5 py-0.5 text-[11px] leading-none ${info.pill}`}>
                                                                        {info.name}
                                                                    </span>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : <span className="text-slate-400">—</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    {showUnimodModal && (
                        <Modal title="UniMod identifiers" onClose={() => setShowUnimodModal(false)}>
                            <div className="space-y-4 text-sm text-slate-700">
                                <p>
                                    The <b>Modification</b> column lists accession IDs from the
                                    {" "}<a href="https://www.unimod.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">UniMod</a>{" "}
                                    database that describe each post-translational modification.
                                </p>
                                <p>
                                    In mass spectrometry searches the search engine identifies
                                    modifications by their <b>mass shift</b> — it cannot distinguish
                                    chemically different modifications that share the same monoisotopic
                                    mass (or whose masses fall within the instrument's precision).
                                    This applies to both closed and open (DMO) searches.
                                </p>
                                <p>
                                    The pipeline therefore groups every UniMod entry that shares the same
                                    amino-acid specificity, position scope, and mass into a single
                                    {" "}<em>mass class</em>.  When a site is assigned to one of these
                                    classes, <b>all</b> candidate UniMod accessions in that class are
                                    shown as equally valid matches.
                                </p>
                                <p className="text-xs text-slate-500">
                                    If only one UniMod accession exists for a given mass class, only that
                                    single ID is displayed.
                                </p>
                            </div>
                        </Modal>
                    )}
                    {residueDetail && (
                        <Modal title={`Modifications at residue ${residueDetail.aa} ${residueDetail.pos}`} onClose={() => setSelectedPos(null)}>
                            <div className="space-y-4 text-sm text-slate-700">
                                <div className="break-all rounded border border-slate-200 bg-slate-50 p-3 font-mono text-base leading-loose">
                                    {residueDetail.hasBefore && <span className="text-slate-400">…</span>}
                                    {residueDetail.before}
                                    <span className="rounded bg-sky-100 px-1 font-bold text-sky-900">{residueDetail.aa}</span>
                                    {residueDetail.after}
                                    {residueDetail.hasAfter && <span className="text-slate-400">…</span>}
                                </div>
                                {residueDetail.mods.length === 0 ? (
                                    <div className="rounded border border-dashed border-slate-300 bg-slate-50 p-4 text-slate-500">
                                        No modifications currently reported at this residue — the selected categories/types may hide them.
                                    </div>
                                ) : (
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-slate-200 text-left text-slate-500">
                                                <th className="py-1 pr-3 font-medium">Symbol</th>
                                                <th className="py-1 pr-3 font-medium">Modification</th>
                                                <th className="py-1 pr-3 font-medium text-right">Mass</th>
                                                <th className="py-1 pr-3 font-medium">Category</th>
                                                <th className="py-1 pr-3 font-medium text-right">PSMs</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {residueDetail.mods.map(m => {
                                                const t = typesByKey.get(modKey(m));
                                                const cat = catInfo(m.mod_category);
                                                const flags = t ? t.flags : [];
                                                return (
                                                    <tr key={m.protmod_id} className="border-b border-slate-100 last:border-0">
                                                        <td className="py-1 pr-3 text-center text-lg" style={{color: cat.color}}>
                                                            {t ? t.shape : '●'}
                                                        </td>
                                                        <td className="py-1 pr-3">
                                                            {t && t.unimod_ids.length > 0 ? (
                                                                <span className="flex flex-wrap gap-x-1">
                                                                    {t.unimod_ids.map(uid => (
                                                                        <a key={uid}
                                                                           href={t.unimod_id === uid && t.unimod_link ? t.unimod_link : `https://www.unimod.org/modifications_view.php?editid1=${uid}`}
                                                                           target="_blank" rel="noopener noreferrer"
                                                                           title={unimodIdToName.get(Number(uid)) || t.full_name || m.full_name || "View on unimod.org"}
                                                                           className="text-blue-600 hover:underline">
                                                                            U:{uid}
                                                                        </a>
                                                                    ))}
                                                                </span>
                                                            ) : <span>{m.full_name || "Unknown"}</span>}
                                                            {flags.length > 0 && (
                                                                <span className="ml-2 inline-flex flex-wrap gap-1 align-middle">
                                                                    {flags.map(f => {
                                                                        const info = FLAG_INFO[f] || {name: f, pill: 'border-slate-300 bg-slate-50 text-slate-600'};
                                                                        return (
                                                                            <span key={f} className={`rounded-full border px-1.5 py-0.5 text-[11px] leading-none ${info.pill}`}>
                                                                                {info.name}
                                                                            </span>
                                                                        );
                                                                    })}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="py-1 pr-3 text-right font-mono">{typeof m.mass === 'number' ? m.mass.toFixed(4) : <span className="text-slate-400">—</span>}</td>
                                                        <td className="py-1 pr-3">
                                                            <span className="inline-flex items-center gap-1.5">
                                                                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{background: cat.color}} />
                                                                {cat.name}
                                                            </span>
                                                        </td>
                                                        <td className="py-1 pr-3 text-right font-mono">{m.psm_count ?? <span className="text-slate-400">—</span>}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </Modal>
                    )}
                </div>
            )}

            {/* Modification detection heatmap across studies/experiments,
                shown under the lollipop figure. */}
            {isReady && L > 0 && prot_mods.length > 0 && (
                <ModHeatmap species_sn={species_sn} protein_seq_id={protein_seq_id} selectedUnimodIds={selectedUnimodIds} />
            )}
        </div>
    );
};

export default PtmViewer;
